// Added by Sejune Oh on 2019-07-01
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { FC, useState, createRef, useEffect } from 'react'
import { createStyles, Theme, makeStyles } from '@material-ui/core'
import clsx from 'clsx'

import Intl from '@/helper/Intl'

import { ExpandLessIcon, ExpandMoreIcon, Typography } from '@/components/_primes'
import MgmtComponentFormStyle from '@/styles/management/MgmtComponentFormStyle'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
interface CollapsibleFormFCProps {
  /** 타이틀 (좌측)  */
  title?: string
  /** Section description  */
  description?: string | JSX.Element
  /** 초기 여/닫힘 상태  */
  initialCollapsedState?: boolean
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const ICON_SIZE = '32px'
const PADDING_SPACES = 32

/**
 * MGMT > EDIT 페이지에서 사용하는 accordion ui 입니다.<br />
 * - title과 Content는 Parent DOM의 Width 기준으로 3:7 비율입니다.<br />
 * - title의 Default value : htmljs_react.component.collapsibleform.title
 */
const CollapsibleForm: FC<CollapsibleFormFCProps> = ({
  title: titleParam,
  initialCollapsedState = false,
  description,
  children,
  ...props
}) => {

  const boxRef = createRef<HTMLDivElement>()
  const iconStyle = { fontSize: ICON_SIZE, width: ICON_SIZE, height: ICON_SIZE }
  const title = titleParam || Intl.getIntlText({ id: 'htmljs_react.component.collapsibleform.title' })

  const classes = useStyles()
  const [isCollapsed, setCollapsed] = useState(initialCollapsedState)
  const [innerHeight, setInnerHeight] = useState<string>('0px')

  const getHeight = () => {
    return isCollapsed ? { height: '0' } :  { height: innerHeight }
  }

  useEffect(() => {
    let mounted = true

    setTimeout(() => {
      if (mounted) {
        const childDiv = boxRef.current ? boxRef.current.firstChild as HTMLDivElement : undefined
        const childDivHeight = childDiv ? (childDiv.offsetHeight + PADDING_SPACES) + 'px' : '0px'
        setInnerHeight(childDivHeight)
      }
    }, 0)

    return () => {
      mounted = false
    }

  // eslint-disable-next-line
  }, [children])

  return (
    <div className={clsx(classes.FormWrapper, classes.wrapper)}>
      <div className={clsx(classes.TextWrapper, classes.title, { [classes.textWrapper] : isCollapsed })}>
        <Typography
          variant="h1"
          className={clsx(classes.FormTtitle, { [classes.formTitle] : isCollapsed } )}
        >
          {title}
        </Typography>
        {description && (
          <div
            className={clsx(
              classes.description,
              {
                [classes.opened]: !isCollapsed
              }
            )}
          >
            {typeof description === 'string' ? (
              <Typography
                variant="body1"
                className={clsx(
                  classes.description,
                  {
                    [classes.opened]: !isCollapsed
                  }
                )}
                dangerouslySetInnerHTML={{ __html: description! }}
              />
            ) : (
              description
            )}
          </div>
        )}
      </div>
      <div
        className={classes.ctrWrapper}
        onClick={() => setCollapsed(!isCollapsed)}
        data-cy-btn="collapse">
        {
          isCollapsed
          ? <ExpandMoreIcon style={iconStyle} />
          : <ExpandLessIcon style={iconStyle} />
        }
      </div>
      <div
        ref={boxRef}
        className={clsx(classes.InputWrapper, classes.formWrapper)}
        style={getHeight()}
      >
        {children}
      </div>
    </div>
  )
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    ...MgmtComponentFormStyle(theme),
    wrapper: {
      position: 'relative',
    },
    title: {
      paddingRight: `${theme.spacing()}px`,
    },
    textWrapper: {
      paddingBottom: `${theme.spacing() / 2}px`,
    },
    formTitle: {
      paddingBottom: 0,
    },
    ctrWrapper: {
      color: '#fff',
      cursor: 'pointer',
      position: 'absolute',
      right: '7px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: ICON_SIZE,
      height: ICON_SIZE,
      fontSize: '1.5rem',
    },
    formWrapper: {
      transition: 'height 0.336s',
      height: 0,
      minHeight: 0,
      overflow: 'hidden',
      paddingTop: `${theme.spacing() * 2}px`,
    },
    description: {
      transition: 'max-height 0.336s',
      overflow: 'hidden',
      maxHeight: 0
    },
    opened: {
      maxHeight: '1920px'
    }
  })
)

export default CollapsibleForm
